import type { QueryClient } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

const PENDING_CONFIG_SYNC_CACHE_KEY = "roster_create_pending_config_sync_cache";

export type RosterCreateConfigSyncCacheTarget = {
  readonly queryGroupId: string;
  readonly jobId?: string | null;
  readonly materializedConfigId?: number | null;
  readonly year?: number | null;
  readonly month?: number | null;
  readonly effects?: {
    readonly nurses?: boolean;
    readonly monthlyLimits?: boolean;
    readonly wanted?: boolean;
    readonly weeklyOff?: boolean;
  };
};

export function markPendingRosterCreateConfigSyncCache(
  target: RosterCreateConfigSyncCacheTarget
) {
  sessionStorage.setItem(PENDING_CONFIG_SYNC_CACHE_KEY, JSON.stringify(target));
}

export function clearPendingRosterCreateConfigSyncCache() {
  sessionStorage.removeItem(PENDING_CONFIG_SYNC_CACHE_KEY);
}

export function consumePendingRosterCreateConfigSyncCache():
  | RosterCreateConfigSyncCacheTarget
  | null {
  const rawValue = sessionStorage.getItem(PENDING_CONFIG_SYNC_CACHE_KEY);
  if (!rawValue) return null;

  clearPendingRosterCreateConfigSyncCache();

  try {
    return JSON.parse(rawValue) as RosterCreateConfigSyncCacheTarget;
  } catch {
    return null;
  }
}

export async function invalidateRosterCreateConfigSyncCaches(
  queryClient: QueryClient,
  target: RosterCreateConfigSyncCacheTarget
) {
  const { effects, month, queryGroupId, year } = target;
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.config.configData(target.queryGroupId),
    }),
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.config.configVersions(target.queryGroupId),
    }),
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.config.rosterConfigVersionsScope(),
    }),
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.shifts.shiftManagerScope(queryGroupId),
    }),
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.manpower.scope() }),
    effects?.nurses
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.listScope(queryGroupId),
        })
      : Promise.resolve(),
    effects?.nurses
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.fullSchema(queryGroupId),
        })
      : Promise.resolve(),
    effects?.monthlyLimits
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.monthlyLimitsScope(queryGroupId),
        })
      : Promise.resolve(),
    effects?.wanted && year != null && month != null
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.adjustment(year, month),
        })
      : Promise.resolve(),
    effects?.wanted && year != null && month != null
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.status(year, month, queryGroupId),
        })
      : Promise.resolve(),
    effects?.wanted && year != null && month != null
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, queryGroupId),
        })
      : Promise.resolve(),
    effects?.wanted && year != null && month != null
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.config(year, month, queryGroupId),
        })
      : Promise.resolve(),
    effects?.weeklyOff
      ? queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.weeklyOff.scope(),
        })
      : Promise.resolve(),
  ]);
}
